import type { ConvertOptions as StreamdownConvertOptions } from '../../../../../lib/markdown/streamdown';

type ConvertOptionsAlias = StreamdownConvertOptions;
export type { ConvertOptionsAlias as ConvertOptions };

type AnalysisSection = 'overview' | 'personality' | 'contentFocus' | 'audienceInteraction';

// eslint-disable-next-line security/detect-unsafe-regex
const OVERVIEW_LABEL_REGEX = /(^overview)(:|\s+[-–—]|\s+)|^overview\b/i;
// eslint-disable-next-line security/detect-unsafe-regex
const PERSONALITY_LABEL_REGEX = /(^personality\s*&?\s*communication(?:\s*style)?)(:|\s+[-–—]|\s+)|^personality\b/i;
// eslint-disable-next-line security/detect-unsafe-regex
const CONTENT_EXPERTISE_LABEL_REGEX = /(^content\s*&?\s*expertise)(:|\s+[-–—]|\s+)|^content\b/i;
// eslint-disable-next-line security/detect-unsafe-regex
const CONTENT_EXPERIENCE_LABEL_REGEX = /(^content\s*&\s*experience)(:|\s+[-–—]|\s+)/i;
// eslint-disable-next-line security/detect-unsafe-regex
const AUDIENCE_INTERACTION_LABEL_REGEX =
  /(^audience\s*(interaction|engagement))(?:\s*style)?(:|\s+[-–—]|\s+)|^audience\b/i;

const SECTION_LABEL_PATTERNS: Record<AnalysisSection, readonly RegExp[]> = {
  overview: [OVERVIEW_LABEL_REGEX],
  personality: [PERSONALITY_LABEL_REGEX],
  contentFocus: [CONTENT_EXPERTISE_LABEL_REGEX, CONTENT_EXPERIENCE_LABEL_REGEX],
  audienceInteraction: [AUDIENCE_INTERACTION_LABEL_REGEX],
} as const;

export function stripLeadingMarkdownHeading(section: AnalysisSection, text: string): string {
  if (!text) {
    return text;
  }
  let t = String(text).trimStart();

  // Remove generic leading markdown hashes (e.g., #, ##, ###) with optional whitespace
  // eslint-disable-next-line security/detect-unsafe-regex
  t = t.replaceAll(/^#{1,6}\s*/g, '');

  // Remove repeated section labels (case-insensitive), optionally followed by punctuation
  for (const rx of SECTION_LABEL_PATTERNS[section]) {
    t = t.replace(rx, '');
  }

  return t.trimStart();
}

export function formatGenerationTime(ms: number | undefined) {
  if (!ms) {
    return null;
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function formatTimeRange(dateRange: { min?: string; max?: string } | undefined) {
  if (!(dateRange?.min && dateRange?.max)) {
    return null;
  }
  const start = new Date(dateRange.min);
  const end = new Date(dateRange.max);
  const diffMs = end.getTime() - start.getTime();
  const diffHours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)));
  const diffDays = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  if (diffHours < 24) {
    return `${diffHours}h`;
  }
  if (diffDays <= 30) {
    return `${diffDays}d`;
  }
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo`;
}

export function formatDateRangeLabel(dateRange: { min?: string; max?: string } | undefined) {
  if (!(dateRange?.min && dateRange?.max)) {
    return null;
  }

  const start = new Date(dateRange.min);
  const end = new Date(dateRange.max);
  const startTime = start.getTime();
  const endTime = end.getTime();

  if (!(Number.isFinite(startTime) && Number.isFinite(endTime))) {
    return null;
  }

  if (endTime < startTime) {
    return null;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  const yearFormatter = new Intl.DateTimeFormat('en-US', { year: 'numeric' });
  const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });
  const dayFormatter = new Intl.DateTimeFormat('en-US', { day: 'numeric' });
  const monthDayFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const monthDayYearFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  let rangeLabel: string;
  if (sameMonth) {
    const monthLabel = monthFormatter.format(start);
    const startDay = dayFormatter.format(start);
    const endDay = dayFormatter.format(end);
    const yearLabel = yearFormatter.format(end);
    rangeLabel = `${monthLabel} ${startDay}–${endDay}, ${yearLabel}`;
  } else if (sameYear) {
    const startLabel = monthDayFormatter.format(start);
    const endLabel = monthDayFormatter.format(end);
    const yearLabel = yearFormatter.format(end);
    rangeLabel = `${startLabel} – ${endLabel}, ${yearLabel}`;
  } else {
    const startLabel = monthDayYearFormatter.format(start);
    const endLabel = monthDayYearFormatter.format(end);
    rangeLabel = `${startLabel} – ${endLabel}`;
  }

  const diffDays = Math.max(1, Math.round((endTime - startTime) / (1000 * 60 * 60 * 24)));

  let durationLabel: string;
  if (diffDays >= 365) {
    const years = Math.round(diffDays / 365);
    durationLabel = years === 1 ? '1 year' : `${years} years`;
  } else if (diffDays >= 90) {
    const months = Math.round(diffDays / 30);
    durationLabel = months === 1 ? '1 month' : `${months} months`;
  } else if (diffDays >= 14) {
    const weeks = Math.round(diffDays / 7);
    durationLabel = weeks === 1 ? '1 week' : `${weeks} weeks`;
  } else {
    durationLabel = diffDays === 1 ? '1 day' : `${diffDays} days`;
  }

  return { rangeLabel, durationLabel };
}

export {
  buildStreamingMarkdownText,
  convertTweetReferences,
  convertTweetReferencesToMarkdown,
  linkifyMentionsToHtml,
} from '../../../../../lib/markdown/streamdown';
